import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../api/axiosConfig';
import NoteCard from './NoteCard';
import NoteModal from './NoteModal';
import AddDocumentModal from './AddDocumentModal';
import DocumentEditor from './DocumentEditor';
import Settings from './Settings';
import About from './About';
import Sidebar, { SidebarView } from './Sidebar';
import { MobileTabBar, MOBILE_TAB_VIEWS } from './MobileTabBar';
import SharedWithMe from './sharing/SharedWithMe';
import { useShare } from './sharing/useShare';
import { FloatingActionButton } from './FloatingActionButton';
import TagManagementModal from './TagManagementModal';
import Storyboards from './Storyboards';
import Discover from './Discover';
import SearchAutocomplete from './SearchAutocomplete';
import { ImportLocalFilesModal } from './DocumentEditor/modals/ImportLocalFilesModal';
import { ImportGoogleDocsModal } from './DocumentEditor/modals/ImportGoogleDocsModal';
import CreateFolderModal from './CreateFolderModal';
import DocumentExplorer from './DocumentExplorer';
import { useLanguage } from '../i18n/LanguageContext';
import { importLocalFiles, listDocumentFolders, createDocumentFolder, deleteDocumentFolder, renameDocument, updateDocument, updateDocumentFolder, DocumentFolder } from '../api/documentApi';
import './Home.css';

interface HomeProps {}

// Wrap each matched search term in <mark> inside a snippet.
const highlightTerms = (snippet: string, terms: string[]): React.ReactNode => {
  if (!snippet || terms.length === 0) return snippet;
  const escaped = terms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const parts = snippet.split(new RegExp(`(${escaped.join('|')})`, 'ig'));
  const lower = terms.map(t => t.toLowerCase());
  return parts.map((part, i) =>
    lower.includes(part.toLowerCase()) ? <mark key={i}>{part}</mark> : part
  );
};

const Home: React.FC<HomeProps> = () => {
  const { t } = useLanguage();
    // Mobile tab bar state
    const [mobileTab, setMobileTab] = useState(0);
    // Detect mobile browser
    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 700px)').matches;
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Check authentication on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }
    
    // Log frontend version info for debugging
  }, [navigate]);
  
  // Initialize state from URL params
  const [notes, setNotes] = useState<any[]>([]);
  // const [notesPage, setNotesPage] = useState(1); // Removed - tracked via ref instead
  const [notesHasMore, setNotesHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [documents, setDocuments] = useState<any[]>([]);
  const [folders, setFolders] = useState<DocumentFolder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  // Whether a search may reach past what literally matched and offer notes
  // connected by shared tags. Remembered, because it is a way of working
  // rather than a per-search decision.
  const [includeRelated, setIncludeRelated] = useState(
    () => localStorage.getItem('includeRelated') !== 'false'
  );
  const [currentView, setCurrentView] = useState<SidebarView>(() => {
    if (isMobile) {
      return (MOBILE_TAB_VIEWS[mobileTab] || 'notes') as SidebarView;
    }
    const view = searchParams.get('view');
    // 'visualize' merged into 'discover' — map old bookmarks/URLs across.
    if (view === 'visualize') return 'discover';
    return (view === 'notes' || view === 'documents' || view === 'discover' || view === 'storyboards' || view === 'shared' || view === 'tags' || view === 'settings' || view === 'about' || view === 'feedback') ? view : 'notes';
  });
  // A note opened straight from a URL — how an accepted invite to a note
  // lands, and how "Shared with me" opens one.
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(() => !!searchParams.get('noteId'));
  const [editingNoteId, setEditingNoteId] = useState<number | null>(() => {
    const noteId = searchParams.get('noteId');
    return noteId ? parseInt(noteId, 10) : null;
  });
  const [isAddDocumentModalOpen, setIsAddDocumentModalOpen] = useState(false);
  // Folder the next new document goes into; null is the top level
  const [addDocumentFolderId, setAddDocumentFolderId] = useState<number | null>(null);
  const [openStoryboardId, setOpenStoryboardId] = useState<number | null>(() => {
    const boardId = searchParams.get('boardId');
    return boardId ? parseInt(boardId, 10) : null;
  });
  const [openDocumentId, setOpenDocumentId] = useState<number | null>(() => {
    const docId = searchParams.get('docId');
    return docId ? parseInt(docId, 10) : null;
  });
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const notesPageRef = useRef(1); // Track current page with ref to avoid stale closures
  const isLoadingRef = useRef(false); // Track loading state with ref
  
  // Import functionality
  const [discoverEntryId, setDiscoverEntryId] = useState<number | null>(null);
  const [showImportMenu, setShowImportMenu] = useState(false);
  const [showImportLocalFilesModal, setShowImportLocalFilesModal] = useState(false);
  const [showImportGoogleDocsModal, setShowImportGoogleDocsModal] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const importMenuRef = useRef<HTMLDivElement>(null);
  

  // Load user theme preference on mount
  useEffect(() => {
    const loadUserTheme = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await api.get('/user/settings/');
        if (response.data.theme) {
          document.documentElement.setAttribute('data-theme', response.data.theme);
        }
      } catch (error: any) {
        console.error('Failed to load user theme:', error);
        
        // Handle authentication errors
        if (error.response?.status === 401 || error.response?.status === 403) {
          localStorage.removeItem('token');
          navigate('/login', { replace: true });
          return;
        }
        
        // Default to dark theme
        document.documentElement.setAttribute('data-theme', 'dark');
      }
    };
    loadUserTheme();
  }, [navigate]);

  // Define loadLatestNotes before it's used in useEffect hooks
  const loadLatestNotes = useCallback(async (pageNum: number = 1, append: boolean = false) => {
    // Prevent duplicate requests
    if (isLoadingRef.current) {
      return;
    }
    
    
    isLoadingRef.current = true;
    
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    
    try {
      const response = await api.get(`/entries/latest/?page=${pageNum}&page_size=20`);
      
      
      const responseData = response.data;
      const newNotes = Array.isArray(responseData.results) ? responseData.results : [];
      
      
      if (append) {
        setNotes(prev => {
          const combined = [...prev, ...newNotes];
          return combined;
        });
      } else {
        setNotes(newNotes);
      }
      
      setNotesHasMore(responseData.has_more || false);
      // setNotesPage(pageNum); // Not needed - using ref instead
      notesPageRef.current = pageNum; // Update ref immediately
      
    } catch (error: any) {
      console.error('Failed to load notes:', error);
      
      // Handle authentication errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('token');
        navigate('/login', { replace: true });
        return;
      }
      
      if (!append) {
        setNotes([]);
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
      isLoadingRef.current = false;
    }
  }, [navigate]); // Include navigate in dependencies

  // Close import menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (importMenuRef.current && !importMenuRef.current.contains(event.target as Node)) {
        setShowImportMenu(false);
      }
    };

    if (showImportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showImportMenu]);

  // Load data based on current view
  useEffect(() => {
    if (currentView === 'notes') {
      notesPageRef.current = 1; // Reset page ref
      loadLatestNotes(1, false);
    } else {
      loadDocuments();
    }
  }, [currentView, loadLatestNotes]);

  // Infinite scroll handler for notes
  useEffect(() => {
    if (currentView !== 'notes' || searchQuery) return;

    const handleScroll = () => {
      // Check if we're near the bottom of the page
      const scrollPosition = window.innerHeight + window.scrollY;
      const scrollHeight = document.documentElement.scrollHeight;

      // Load more when user scrolls within 300px of bottom
      if (scrollHeight - scrollPosition < 300 && notesHasMore && !isLoadingRef.current) {
        loadLatestNotes(notesPageRef.current + 1, true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentView, searchQuery, notesHasMore, loadLatestNotes]);

  const performSearch = useCallback(async () => {
    setIsLoading(true);
    try {
      
      if (currentView === 'documents') {
        // Search documents
        const response = await api.get('/documents/search/', {
          params: { q: searchQuery },
          });
        // console.log('Documents search response:', response.data);
        setDocuments(Array.isArray(response.data) ? response.data : []);
      } else {
        // Search notes/entries
        const response = await api.get('/entries/search/', {
          params: { q: searchQuery, include_related: includeRelated },
          });
        // console.log('Notes search response:', response.data);
        setNotes(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error('Search failed:', error);
      if (currentView === 'documents') {
        setDocuments([]);
      } else {
        setNotes([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [currentView, searchQuery, includeRelated]);

  // Handle search with debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.trim() === '') {
      if (currentView === 'notes') {
        notesPageRef.current = 1;
        loadLatestNotes(1, false);
      } else {
        loadDocuments();
      }
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      performSearch();
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, currentView, performSearch, loadLatestNotes]);

  const loadDocuments = async () => {
    setIsLoading(true);
    try {
      
      // Load both folders and documents in parallel
      const [documentsResponse, foldersResponse] = await Promise.all([
        api.get('/documents/'),
        listDocumentFolders()
      ]);
      
      // console.log('Documents response:', documentsResponse.data);
      // console.log('Folders response:', foldersResponse);
      setDocuments(Array.isArray(documentsResponse.data) ? documentsResponse.data : []);
      setFolders(Array.isArray(foldersResponse) ? foldersResponse : []);
    } catch (error) {
      console.error('Failed to load documents/folders:', error);
      setDocuments([]);
      setFolders([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    try {
      await api.delete(`/entries/${noteId}/`);
      
      // Remove from local state
      setNotes(notes.filter(note => note.id !== noteId));
    } catch (error) {
      console.error('Failed to delete note:', error);
    }
  };

  const handleEditNote = (noteId: number) => {
    setEditingNoteId(noteId);
    setIsNoteModalOpen(true);
  };

  const handleAddNoteSuccess = () => {
    notesPageRef.current = 1;
    loadLatestNotes(1, false);
  };

  const handleEditNoteSuccess = () => {
    notesPageRef.current = 1;
    loadLatestNotes(1, false);
    setEditingNoteId(null);
  };

  const handleViewChange = (view: SidebarView) => {
    setCurrentView(view);
    setSearchQuery('');
    setOpenStoryboardId(null);
    setSearchParams({ view });
  };

  // Opening one of someone else's things from "Shared with me". Documents and
  // boards go to their editors; a note opens in the note modal, which decides
  // for itself whether it may be edited.
  const handleOpenStoryboard = (storyboardId: number) => {
    setOpenStoryboardId(storyboardId);
    setCurrentView('storyboards');
    setSearchParams({ view: 'storyboards', boardId: storyboardId.toString() });
  };

  const handleOpenSharedNote = (noteId: number) => {
    setEditingNoteId(noteId);
    setIsNoteModalOpen(true);
  };

  // "See the threads" on a card: jump into Discovery centered on that note.
  const handleSeeThreads = useCallback((entryId: number) => {
    setDiscoverEntryId(entryId);
    setCurrentView('discover');
    setSearchQuery('');
    setSearchParams({ view: 'discover' });
  }, [setSearchParams]);

  const handleDiscoverEntryConsumed = useCallback(() => setDiscoverEntryId(null), []);

  // Sharing a note is driven from its card menu; the dialog needs to know
  // which note before it opens, so the id is state rather than an argument.
  const [shareNote, setShareNote] = useState<{ id: number; name: string } | null>(null);
  const { openShare: openNoteShare, shareUi: noteShareUi } =
    useShare('note', shareNote?.id ?? null, shareNote?.name || '');

  const handleShareNote = useCallback((note: any) => {
    const title = (note.content_preview || note.content || '')
      .replace(/<[^>]*>/g, ' ').replace(/\[\[image\]\]/g, ' ').replace(/\s+/g, ' ').trim();
    setShareNote({ id: note.id, name: title.slice(0, 40) || t.home.untitledNote });
    openNoteShare();
  }, [openNoteShare, t]);

  const handleAddButtonClick = () => {
    if (currentView === 'documents') {
      setIsAddDocumentModalOpen(true);
    } else {
      setEditingNoteId(null); // null means create mode
      setIsNoteModalOpen(true);
    }
  };

  const handleOpenDocument = (documentId: number) => {
    setOpenDocumentId(documentId);
    // Update URL to include the document ID. A document opened from "Shared
    // with me" belongs to that view, and closing it goes back there.
    setSearchParams({ view: currentView === 'shared' ? 'shared' : 'documents',
      docId: documentId.toString() });
  };

  const handleCloseDocument = () => {
    setOpenDocumentId(null);
    // Clear the docId from URL and return to the list we came from
    setSearchParams({ view: currentView === 'shared' ? 'shared' : 'documents' });
    if (currentView !== 'shared') loadDocuments(); // Refresh documents list
  };

  const handleAddDocumentSuccess = (documentId: number) => {
    // Open the newly created document
    setOpenDocumentId(documentId);
    setSearchParams({ view: 'documents', docId: documentId.toString() });
  };


  const handleDeleteDocument = async (documentId: number) => {
    try {
      await api.delete(`/documents/${documentId}/`);

      // Functional update, not documents.filter: deleting a multi-document
      // selection calls this once per document, and every call after the
      // first would otherwise be working from the list as it was before any
      // of them ran, so only the last deletion would stick.
      setDocuments(prev => prev.filter(doc => doc.id !== documentId));
    } catch (error) {
      console.error('Failed to delete document:', error);
    }
  };

  const handleRenameDocument = async (documentId: number, name: string) => {
    try {
      await renameDocument(documentId, name);
      setDocuments(docs => docs.map(doc =>
        doc.id === documentId ? { ...doc, name } : doc
      ));
    } catch (error) {
      console.error('Failed to rename document:', error);
    }
  };

  const handleCreateFolder = async (folderName: string) => {
    try {
      const newFolder = await createDocumentFolder(folderName);
      setFolders([...folders, newFolder]);
    } catch (error) {
      console.error('Failed to create folder:', error);
      throw error;
    }
  };

  const handleDeleteFolder = async (folderId: number, deleteDocuments = false) => {
    try {
      await deleteDocumentFolder(folderId, deleteDocuments);
      // Subfolders cascade on the backend and the documents inside either moved
      // to the top level or went with the folder — reload both lists rather
      // than trying to mirror that locally.
      await loadDocuments();
    } catch (error) {
      console.error('Failed to delete folder:', error);
    }
  };

  const handleMoveDocumentToFolder = async (documentId: number, folderId: number | null) => {
    try {
      // The serializer only accepts folder moves via the `folder` key
      await updateDocument(documentId, { folder: folderId });
      // Functional update — dragging a selection moves several documents in
      // one gesture, and each move has to see the previous one's result
      setDocuments(prev => prev.map(doc =>
        doc.id === documentId ? { ...doc, folder_id: folderId } : doc
      ));
    } catch (error) {
      console.error('Failed to move document:', error);
      throw error;
    }
  };

  const handleMoveFolder = async (folderId: number, parentId: number | null) => {
    try {
      const updated = await updateDocumentFolder(folderId, { parent: parentId });
      setFolders(folders.map(f => (f.id === folderId ? updated : f)));
    } catch (error) {
      console.error('Failed to move folder:', error);
    }
  };

  const handleCreateSubfolder = async (parentId: number) => {
    const name = window.prompt(t.home.subfolderNamePrompt);
    if (!name || !name.trim()) return;
    try {
      const newFolder = await createDocumentFolder(name.trim(), parentId);
      setFolders(prev => [...prev, newFolder]);
    } catch (error) {
      console.error('Failed to create subfolder:', error);
      alert(t.home.folderCreateFailed);
    }
  };

  const handleImportLocalFiles = async (files: File[]) => {
    try {
      await importLocalFiles(files);
      
      // Refresh documents list
      await loadDocuments();
      
      // Close modal
      setShowImportLocalFilesModal(false);
      setShowImportMenu(false);
    } catch (error) {
      console.error('Import failed:', error);
      throw error; // Let modal handle the error display
    }
  };

  // The modal runs the Picker + import itself; we just refresh on success.
  const handleGoogleDocsImported = async () => {
    await loadDocuments();
    setShowImportMenu(false);
  };

  // Ensure displayedItems is always an array
  const displayedItems = currentView === 'documents' ? documents : notes;
  const safeDisplayedItems = Array.isArray(displayedItems) ? displayedItems : [];
  
  // Debug logging

  // If document editor is open, show that instead
  if (openDocumentId) {
    return <DocumentEditor documentId={openDocumentId} onClose={handleCloseDocument} />;
  }

  // If settings view is active, show Settings instead of main content
  if (currentView === 'settings' || currentView === 'feedback') {
    return (
      <div className="home-container">
        {/* Hide sidebar on mobile */}
        {!isMobile && <Sidebar onAddNoteClick={handleAddButtonClick} currentView={currentView} onViewChange={handleViewChange} onImportClick={() => setShowImportMenu(!showImportMenu)} />}
        <div className="home-content">
          <Settings initialTab={currentView === 'feedback' ? 'help' : 'account'} />
        </div>
        {/* Mobile tab bar at bottom */}
        {isMobile && (
          <MobileTabBar selectedTab={mobileTab} onTabSelect={tab => { setMobileTab(tab); setCurrentView(MOBILE_TAB_VIEWS[tab] as SidebarView); }} />
        )}
      </div>
    );
  }

  // If about view is active, show About instead of main content
  if (currentView === 'about') {
    return (
      <div className="home-container">
        {!isMobile && <Sidebar 
          onAddNoteClick={handleAddButtonClick}
          currentView={currentView}
          onViewChange={handleViewChange}
          onImportClick={() => setShowImportMenu(!showImportMenu)}
        />}
        <main className="home-content">
          <About />
        </main>
        {isMobile && (
          <MobileTabBar selectedTab={mobileTab} onTabSelect={tab => { setMobileTab(tab); setCurrentView(MOBILE_TAB_VIEWS[tab] as SidebarView); }} />
        )}
      </div>
    );
  }

  // If tags view is active, show TagManagementModal instead of main content
  if (currentView === 'tags') {
    return (
      <div className="home-container">
        {!isMobile && <Sidebar 
          onAddNoteClick={handleAddButtonClick}
          currentView={currentView}
          onViewChange={handleViewChange}
          onImportClick={() => setShowImportMenu(!showImportMenu)}
        />}
        <main className="home-content">
          <TagManagementModal 
            isOpen={true}
            asFullView={true}
            onClose={() => handleViewChange('notes')}
            onTagsUpdated={() => {
              // Refresh notes when tags are updated
              if (searchQuery) {
                performSearch();
              } else {
                notesPageRef.current = 1;
                loadLatestNotes(1, false);
              }
            }}
          />
        </main>
        {isMobile && (
          <MobileTabBar selectedTab={mobileTab} onTabSelect={tab => { 
            setMobileTab(tab); 
            setCurrentView(MOBILE_TAB_VIEWS[tab] as SidebarView); 
          }} />
        )}
      </div>
    );
  }

  if (currentView === 'discover') {
    return (
      <div className="home-container">
        {!isMobile && <Sidebar onAddNoteClick={handleAddButtonClick} currentView={currentView} onViewChange={handleViewChange} onImportClick={() => setShowImportMenu(!showImportMenu)} />}
        <main className="home-content">
          <Discover
            initialEntryId={discoverEntryId}
            onInitialEntryConsumed={handleDiscoverEntryConsumed}
          />
        </main>
        {isMobile && (
          <MobileTabBar selectedTab={mobileTab} onTabSelect={tab => {
            setMobileTab(tab);
            setCurrentView(MOBILE_TAB_VIEWS[tab] as SidebarView);
          }} />
        )}
      </div>
    );
  }

  // Things other people have shared. They are kept out of the lists above on
  // purpose, so this page is the only way to reach them.
  if (currentView === 'shared') {
    return (
      <div className="home-container">
        {!isMobile && <Sidebar
          onAddNoteClick={handleAddButtonClick}
          currentView={currentView}
          onViewChange={handleViewChange}
          onImportClick={() => setShowImportMenu(!showImportMenu)}
        />}
        <main className="home-content">
          <SharedWithMe
            onOpenDocument={handleOpenDocument}
            onOpenStoryboard={handleOpenStoryboard}
            onOpenNote={handleOpenSharedNote}
          />
        </main>
        <NoteModal
          isOpen={isNoteModalOpen}
          noteId={editingNoteId}
          onClose={() => {
            setIsNoteModalOpen(false);
            setEditingNoteId(null);
          }}
          onSuccess={() => setIsNoteModalOpen(false)}
        />
        {isMobile && (
          <MobileTabBar selectedTab={mobileTab} onTabSelect={tab => {
            setMobileTab(tab);
            setCurrentView(MOBILE_TAB_VIEWS[tab] as SidebarView);
          }} />
        )}
      </div>
    );
  }

  // If storyboards view is active, show Storyboards instead of main content
  if (currentView === 'storyboards') {
    return (
      <div className="home-container">
        {!isMobile && <Sidebar 
          onAddNoteClick={handleAddButtonClick}
          currentView={currentView}
          onViewChange={handleViewChange}
          onImportClick={() => setShowImportMenu(!showImportMenu)}
        />}
        <main className="home-content">
          <Storyboards
            onRequestCreate={isMobile ? () => {} : undefined}
            initialStoryboardId={openStoryboardId}
          />
        </main>
        {isMobile && (
          <>
            <MobileTabBar selectedTab={mobileTab} onTabSelect={tab => { 
              setMobileTab(tab); 
              setCurrentView(MOBILE_TAB_VIEWS[tab] as SidebarView); 
            }} />
            <FloatingActionButton 
              icon="add-storyboard" 
              onClick={() => {
                if ((window as any).__openStoryboardModal) {
                  (window as any).__openStoryboardModal();
                }
              }}
              position="primary"
            />
          </>
        )}
      </div>
    );
  }

  return (
    <div className="home-container">
      <Sidebar 
        onAddNoteClick={handleAddButtonClick}
        currentView={currentView}
        onViewChange={handleViewChange}
        onImportClick={() => setShowImportMenu(!showImportMenu)}
      />
      
      <main className="home-content">
        <div className="home-header">
          <SearchAutocomplete
            className="search-input"
            placeholder={currentView === 'documents' ? t.home.searchDocumentsPlaceholder : t.home.searchNotesPlaceholder}
            value={searchQuery}
            onChange={setSearchQuery}
          />

          {/* Only meaningful while searching — with no query there is nothing
              to reach out from. */}
          {currentView === 'notes' && searchQuery.trim() && (
            <label className="include-related-toggle" title={t.home.includeRelatedHint}>
              <input
                type="checkbox"
                checked={includeRelated}
                onChange={(e) => {
                  setIncludeRelated(e.target.checked);
                  localStorage.setItem('includeRelated', String(e.target.checked));
                }}
              />
              {t.home.includeRelated}
            </label>
          )}
          
          {/* Create Folder button for documents view */}
          {currentView === 'documents' && (
            <button 
              className="create-folder-button"
              onClick={() => setShowCreateFolderModal(true)}
              title={t.home.createNewFolder}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                <line x1="12" y1="11" x2="12" y2="17"></line>
                <line x1="9" y1="14" x2="15" y2="14"></line>
              </svg>
              {t.home.newFolder}
            </button>
          )}
          
          {/* "Create Document from Notes" moved into the storyboard editor,
              where the board's order drives the document */}
        </div>

        <div className="feed">
          {isLoading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>{t.home.loading}</p>
            </div>
          ) : safeDisplayedItems.length === 0 ? (
            <div className="empty-state">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              <h3>{currentView === 'documents' ? t.home.noDocuments : t.home.noNotes}</h3>
              <p>
                {searchQuery
                  ? t.home.tryDifferentSearch
                  : currentView === 'documents'
                    ? t.home.createFirstDocument
                    : t.home.clickAddNote}
              </p>
            </div>
          ) : (
            <div className={currentView === 'documents' ? 'documents-list' : 'notes-grid'}>
              {currentView === 'documents' ? (
                <DocumentExplorer
                  documents={documents}
                  folders={folders}
                  flat={searchQuery.trim().length > 0}
                  onOpenDocument={handleOpenDocument}
                  onDeleteDocument={handleDeleteDocument}
                  onDeleteFolder={handleDeleteFolder}
                  onMoveDocument={handleMoveDocumentToFolder}
                  onMoveFolder={handleMoveFolder}
                  onCreateSubfolder={handleCreateSubfolder}
                  onCreateDocumentInFolder={(folderId) => {
                    setAddDocumentFolderId(folderId);
                    setIsAddDocumentModalOpen(true);
                  }}
                  onRenameDocument={handleRenameDocument}
                />
              ) : (
                safeDisplayedItems.map((item, index) => {
                  const match = searchQuery.trim() ? (item as any).match : null;
                  const prevRelated = index > 0 && (safeDisplayedItems[index - 1] as any).match?.related;
                  const showRelatedDivider = !!match?.related && !prevRelated;
                  const card = (
                    <NoteCard
                      key={item.id}
                      note={item}
                      onEdit={handleEditNote}
                      onDelete={handleDeleteNote}
                      onSeeThreads={handleSeeThreads}
                      onShare={handleShareNote}
                    />
                  );
                  if (!match) return card;
                  return (
                    <React.Fragment key={item.id}>
                      {showRelatedDivider && (
                        <div className="search-related-divider">
                          {t.home.relatedNotes}
                        </div>
                      )}
                      <div className="search-result">
                        {(match.tags?.length > 0 || match.snippet) && !match.related && (
                          <div className="search-match-strip">
                            {match.tags?.length > 0 && (
                              <span className="search-match-tags">
                                {match.tags.slice(0, 3).map((tag: any) => (
                                  <span
                                    key={tag.id}
                                    className="search-match-tag"
                                    style={{ backgroundColor: tag.color, color: tag.text_color }}
                                    title={tag.quality === 'fuzzy' ? t.home.closeMatch : t.home.matchedTag}
                                  >
                                    {tag.quality === 'fuzzy' ? `≈ ${tag.name}` : tag.name}
                                  </span>
                                ))}
                              </span>
                            )}
                            {match.snippet && (
                              <span className="search-match-snippet">
                                {match.in_attachment && (
                                  <span className="search-match-source">{t.home.inPdf}</span>
                                )}
                                {highlightTerms(match.snippet, match.terms || [])}
                              </span>
                            )}
                          </div>
                        )}
                        {match.related && match.tags?.length > 0 && (
                          <div className="search-match-strip search-match-strip-related">
                            <span className="search-connected-by">{t.home.connectedBy}</span>
                            <span className="search-match-tags">
                              {match.tags.slice(0, 3).map((tag: any) => (
                                <span
                                  key={tag.id}
                                  className="search-match-tag"
                                  style={{ backgroundColor: tag.color, color: tag.text_color }}
                                >
                                  {tag.name}
                                </span>
                              ))}
                            </span>
                          </div>
                        )}
                        {card}
                      </div>
                    </React.Fragment>
                  );
                })
              )}
            </div>
          )}
          
          {/* Loading more indicator */}
          {isLoadingMore && currentView === 'notes' && (
            <div className="loading-more">
              <div className="spinner"></div>
              <p>{t.home.loadingMoreNotes}</p>
            </div>
          )}
        </div>
      </main>

      {/* Import menu popup */}
      {showImportMenu && (
        <div className="import-menu-overlay" ref={importMenuRef}>
          <div className="import-menu">
            <button 
              className="import-menu-option"
              onClick={() => {
                setShowImportLocalFilesModal(true);
                setShowImportMenu(false);
              }}
            >
              <span className="import-menu-icon">💻</span>
              <div className="import-menu-text">
                <div className="import-menu-title">{t.home.importLocal}</div>
                <div className="import-menu-subtitle">{t.home.importLocalDesc}</div>
              </div>
            </button>
            <button 
              className="import-menu-option"
              onClick={() => {
                setShowImportGoogleDocsModal(true);
                setShowImportMenu(false);
              }}
            >
              <span className="import-menu-icon">📄</span>
              <div className="import-menu-text">
                <div className="import-menu-title">{t.home.importGoogleDocs}</div>
                <div className="import-menu-subtitle">{t.home.importGoogleDocsDesc}</div>
              </div>
            </button>
          </div>
        </div>
      )}

      <NoteModal
        isOpen={isNoteModalOpen}
        noteId={editingNoteId}
        onClose={() => {
          setIsNoteModalOpen(false);
          setEditingNoteId(null);
        }}
        onSuccess={() => {
          if (editingNoteId) {
            handleEditNoteSuccess();
          } else {
            handleAddNoteSuccess();
          }
        }}
      />

      {noteShareUi}

      <AddDocumentModal
        isOpen={isAddDocumentModalOpen}
        onClose={() => {
          setIsAddDocumentModalOpen(false);
          setAddDocumentFolderId(null);
        }}
        onSuccess={handleAddDocumentSuccess}
        folderId={addDocumentFolderId}
      />

      {showImportLocalFilesModal && (
        <ImportLocalFilesModal
          t={t}
          onClose={() => setShowImportLocalFilesModal(false)}
          onImport={handleImportLocalFiles}
        />
      )}

      {showImportGoogleDocsModal && (
        <ImportGoogleDocsModal
          t={t}
          onClose={() => setShowImportGoogleDocsModal(false)}
          onImported={handleGoogleDocsImported}
        />
      )}

      <CreateFolderModal
        isOpen={showCreateFolderModal}
        onClose={() => setShowCreateFolderModal(false)}
        onCreate={handleCreateFolder}
      />

      {/* Floating Action Buttons - Mobile Only for Notes and Documents views */}
      {isMobile && currentView === 'notes' && (
        <FloatingActionButton 
          icon="add-note" 
          onClick={handleAddButtonClick}
          position="primary"
        />
      )}
      
      {isMobile && currentView === 'documents' && (
        <>
          <FloatingActionButton 
            icon="add-document" 
            onClick={() => setIsAddDocumentModalOpen(true)}
            position="primary"
          />
          <FloatingActionButton 
            icon="add-folder" 
            onClick={() => setShowCreateFolderModal(true)}
            position="secondary"
          />
        </>
      )}

      {/* Mobile Tab Bar */}
      {isMobile && (
        <MobileTabBar 
          selectedTab={mobileTab} 
          onTabSelect={tab => { 
            setMobileTab(tab); 
            setCurrentView(MOBILE_TAB_VIEWS[tab] as SidebarView); 
          }} 
        />
      )}
    </div>
  );
};

export default Home;
